import moment from 'moment';
import { toJS } from 'mobx';
import { AxiosInstance } from 'axios';
import { fromPromise, IPromiseBasedObservable } from 'mobx-utils';
import searchico from 'searchico';
import { WidgetDimensionKey } from 'utils/Widget/WidgetDimensionKey';
import { GraphQLService } from '../GraphQLService/GraphQLService';
import { MonitoringDashboard } from '../PostsService/PostsService';
import { Cache } from 'utils/Cache/Cache';
import ObjectUtils from 'utils/ObjectUtils/ObjectUtils';
import { BulkLookupValue, ReportingLookupFilter } from '../SprinklrAPIService/SprinklrAPIService';
import { AnalyticsEngine } from '@sprinklr/stories/analytics/AnalyticsRequest';
import AuthService from '../Auth/AuthService';
import TopicTheme from 'models/Analytics/TopicTheme';
import SLAFrequency from 'models/Analytics/SLAFrequency';
import AccountItem from 'models/Analytics/AccountItem';
import SourceItem from 'models/Analytics/SourceItem';
import CommunityUser from 'models/Analytics/CommunityUser';
import BenchmarkBrand from 'models/Analytics/BenchmarkBrand';
import TrendService from '../TrendService/TrendService';
import TemporalDimension from 'models/Analytics/TemporalDimension';
import { findSorted } from 'utils/ArrayUtils/ArrayUtils';

interface CacheEntry {
    items: Array<any>;
    index: any; // searchico full text index
}

interface DimensionCacheReport {
    all: Array<any>;
    normal: Array<any>;
    normalFilters: Array<any>;
    custom: Array<any>;
    customFilters: Array<any>;
}

type DimensionCacheEngine = { [report: string]: DimensionCacheReport };
type DimensionCache = { [engine: string]: DimensionCacheEngine };

enum FilterType {
    None,
    Filter,
    Measurement,
}

const byDimensionLimits = [150000, 100000, 50000];

export default class MetaInfoService {
    // Promise for loading persistent lookup values
    static loaderDashboards: IPromiseBasedObservable<any>;
    private static loaderDimensions: { [engine: string]: IPromiseBasedObservable<any> } = {};
    static loaderSLAPresets: IPromiseBasedObservable<any>;

    private axios: AxiosInstance;
    private graphQLService: GraphQLService;
    private authService: AuthService;

    private cachePromises: any = {
        LISTENING: null,
        BENCHMARKING: null,
        PLATFORM: null,
        PAID: null,
        UNIFIED_ANALYTICS_REPORTING_ENGINE: null,
        CAMPAIGN: null,
        ADVOCACY: null,
        RDB_FIREHOSE: null,
        TWITTER: new Promise(resolve => {
            resolve({
                TRENDS: {
                    dimensions: [
                        {
                            name: 'TRENDING_TOPIC',
                            fieldName: 'TRENDING_TOPIC',
                            hidden: false,
                            uniqueKey: 'TRENDING_TOPIC',
                            type: 'NORMAL',
                            sortable: true,
                            multiValue: false,
                            multiValuedBackend: false,
                            filterDimension: false,
                            channelTypes: [],
                            allowedAsDrilldown: false,
                            groupByAllowed: true,
                            displayName: 'Trending Topic',
                        },
                        {
                            name: 'WOEID',
                            fieldName: 'WOEID',
                            hidden: false,
                            uniqueKey: 'WOEID',
                            type: 'NORMAL',
                            sortable: false,
                            multiValue: false,
                            multiValuedBackend: false,
                            filterDimension: true,
                            channelTypes: [],
                            allowedAsDrilldown: false,
                            groupByAllowed: false,
                            displayName: 'Location',
                        },
                    ],
                    measurements: [
                        {
                            name: 'MENTIONS',
                            fieldName: 'MENTIONS',
                            hidden: false,
                            uniqueKey: 'MENTIONS',
                            type: 'DELTA',
                            isShared: false,
                            shared: false,
                            displayName: 'Mentions',
                        },
                    ],
                },
            });
        }).then((result: any) => {
            this.cacheDimensionsAndMetrics('TWITTER', result);
        }),
    };

    private cacheMetrics: any = {
        LISTENING: null,
        BENCHMARKING: null,
        PLATFORM: null,
        PAID: null,
        UNIFIED_ANALYTICS_REPORTING_ENGINE: null,
        CAMPAIGN: null,
        ADVOCACY: null,
        TWITTER: null,
        RDB_FIREHOSE: null,
    };

    private cacheDimensions: DimensionCache = {
        LISTENING: null,
        BENCHMARKING: null,
        PLATFORM: null,
        PAID: null,
        UNIFIED_ANALYTICS_REPORTING_ENGINE: null,
        CAMPAIGN: null,
        ADVOCACY: null,
        TWITTER: null,
        RDB_FIREHOSE: null,
    };

    private indexDimensions: any = {
        LISTENING: null,
        BENCHMARKING: null,
        PLATFORM: null,
        PAID: null,
        UNIFIED_ANALYTICS_REPORTING_ENGINE: null,
        CAMPAIGN: null,
        ADVOCACY: null,
        TWITTER: null,
        RDB_FIREHOSE: null,
    };

    private cacheSLAPresets: any = {
        PLATFORM: null,
    };

    private cacheAccounts: any = null;

    private dashboards: Array<MonitoringDashboard> = null;
    private cacheMetricLabel = new Cache<string>();
    private cacheDimensionItem = new Cache<any>();
    private cacheValueLabels = new Cache<Array<BulkLookupValue>>();
    private cacheUnFilterableDimensions: { [key: string]: { [key: string]: BulkLookupValue } } = {};
    private static readonly unFilterableDimensions = {
        LST_THEME: true,
        TOPIC_TAGS: true,
    };

    constructor(axios: AxiosInstance, graphQLService: GraphQLService, authService: AuthService) {
        this.axios = axios;
        this.graphQLService = graphQLService;
        this.authService = authService;
    }

    static getDimensionLoader(engine: AnalyticsEngine): IPromiseBasedObservable<any> {
        let result: IPromiseBasedObservable<any>;

        result = MetaInfoService.loaderDimensions[engine];
        if (!result) {
            result = MetaInfoService.loaderDimensions['TEST'];
        }

        return result;
    }

    // For testing use only
    static setDimensionLoader(promise: IPromiseBasedObservable<any>) {
        MetaInfoService.loaderDimensions['TEST'] = promise;
    }

    static normalizeBulkItem(item: any, dimension?: WidgetDimensionKey): void {
        if (typeof item !== 'object' || item === null) {
            return;
        }

        // BENCHMARKING_ACCOUNT_ID
        if (dimension && dimension.name == 'BENCHMARKING_ACCOUNT_ID') {
            //Try to make BENCHMARKING_ACCOUNT_IDs look like regular account objects
            if (item.type && !item.snType) {
                item.snType = item.type;
            }
            if (item.displayName) {
                item.name = item.displayName;
            }
            return;
        }

        // USER_ID and AFFECTED_USER_ID
        if (item.userId !== undefined) {
            item.id = item.userId.toString();
            // SPRINKLR_CREATIVE_ACCOUNT_DICTIONARY
        } else if (
            dimension?.name === 'SPRINKLR_CREATIVE_ACCOUNT_DICTIONARY' &&
            item.accountUserId !== undefined
        ) {
            item.id = item.accountUserId.toString();
            // ACCOUNT_ID
        } else if (item.accountId !== undefined) {
            item.id = item.accountId.toString();
        }

        if (item.name === undefined || item.name === null) {
            if (item.displayName !== undefined) {
                item.name = item.displayName;
                // USER_ID and AFFECTED_USER_ID
            } else if (item.visibleId !== undefined) {
                item.name = item.visibleId;
                // ACCOUNT_ID
            } else if (item.screenName !== undefined) {
                item.name = item.screenName;
                // ACCOUNT_GROUP_ID
            } else if (item.groupName !== undefined) {
                item.name = item.groupName;
                // Otherwise this record doesn't have a readable name, so use id instead
                //   https://sprinklr.atlassian.net/browse/DISPLAY-1287
            } else {
                item.name = item.id;
            }
        }

        // Cleanup extra crap we don't need.  Reduces the in-memory load on these
        // large data pulls.  DISPLAY-1052
        if (dimension) {
            switch (dimension.name) {
                case 'MEDIA_ASSET_ID':
                    delete item.assetSource;
                    delete item.assetStatus;
                    delete item.assetType;
                    delete item.clientActionStats;
                    delete item.clientDetails;
                    delete item.customFields;
                    delete item.reportingStats;
                    delete item.shareConfigs;
                    delete item.socialAsset;
                    break;
            }
        }
    }

    static normalizeTemporalResponse(result: any[], dimensionName: string) {
        const response = {};

        for (const id in result) {
            response[id] = new TemporalDimension(result[id], id, dimensionName);
        }

        return response;
    }

    static normalizeAccountIds(accountIds: any[]) {
        const response = {};

        for (const id in accountIds) {
            const accountItem = AccountItem.fromObject(accountIds[id]);

            if (accountItem) {
                response[accountItem.accountId] = accountItem;
            }
        }

        return response;
    }

    static normalizeSourceIds(ids: any[]) {
        const response = {};

        for (const id in ids) {
            const item = SourceItem.fromObject(ids[id]);

            if (item) {
                response[item.id] = item;
            }
        }

        return response;
    }

    static normalizeThemeResponse(themes: any[]) {
        const response = {};

        themes.forEach((theme: any) => {
            if (typeof theme !== 'object' || theme === null || !theme.id) {
                return;
            }

            response[theme.id] = new TopicTheme(
                theme.id,
                theme.name,
                theme.displayName,
                theme.imageUrl,
                theme.color
            );
        });

        return response;
    }

    static normalizeBenchmarkingResponse(response: any) {
        if (!response) {
            return;
        }
        const themes = {};

        Object.keys(response).forEach((theme: any) => {
            const item = response[theme];
            if (!item.imageUrl) {
                themes[item.id] = item;
                return;
            }
            themes[item.id] = new BenchmarkBrand(item.id, item.name, item.imageUrl);
        });
        return themes;
    }

    static normalizeSLAFrequencyResponse(response: any) {
        const frequencies = {};

        for (const key in response) {
            const freq = response[key];

            if (!freq.id || !freq.name) {
                continue;
            }

            // Convert a time interval to a to-from value.
            const interval = freq.id.split('-');
            if (interval.length !== 2) {
                continue;
            }

            frequencies[freq.id] = new SLAFrequency(freq.id, freq.name, interval[0], interval[1]);
        }

        return frequencies;
    }

    static normalizeCommunityUsers(users: any[]) {
        const response = {};

        for (const id in users) {
            const communityUser = CommunityUser.fromObject(users[id]);

            if (communityUser) {
                response[id] = communityUser;
            }
        }

        return response;
    }

    isLoaded(engine: AnalyticsEngine) {
        return this.getDimensionCache(engine) !== null;
    }

    isValidReport(engine: AnalyticsEngine, report: string) {
        return this.getDimensionCache(engine, report) !== null;
    }

    getReports(engine: AnalyticsEngine): string[] {
        const cache = this.cacheDimensions[engine];
        if (!cache) {
            return [];
        }
        return Object.keys(cache);
    }

    getDimension(engine: AnalyticsEngine, report: string, dimension: WidgetDimensionKey): any {
        const cache = this.getDimensionCache(engine, report, 'all');

        if (cache) {
            const unique = dimension.toString();

            const cached = this.cacheDimensionItem.get(unique);
            if (cached) {
                return cached;
            } else {
                // Binary search in the sorted dimensions
                const offset = findSorted(cache, item => {
                    return dimension.compareTo(item);
                });

                if (offset != -1) {
                    this.cacheDimensionItem.set(unique, cache[offset]);
                    return cache[offset];
                }
            }
        }

        return null;
    }

    getDimensionLabel(
        engine: AnalyticsEngine,
        report: string,
        dimension: WidgetDimensionKey
    ): string {
        const found = this.getDimension(engine, report, dimension);
        if (found) {
            return found.displayName;
        } else {
            return null;
        }
    }

    getDimensions(
        engine: AnalyticsEngine,
        report: string,
        type: 'all' | 'normal' | 'custom' | 'normalFilters' | 'customFilters'
    ): Array<any> {
        const found = this.getDimensionCache(engine, report, type);
        if (found) {
            return found;
        } else {
            return [];
        }
    }

    findDimensionReports(engine: AnalyticsEngine, dimension: WidgetDimensionKey): string[] {
        const index = this.indexDimensions[engine];

        if (index) {
            return index
                .find(dimension.name)
                .filter(item => item.name === dimension.name)
                .map(item => item.report);
        } else {
            return null;
        }
    }

    getMetrics(engine: AnalyticsEngine): Array<any> {
        if (this.cacheMetrics[engine]) {
            return this.cacheMetrics[engine].items;
        } else {
            return [];
        }
    }

    getMetricsIndex(engine: AnalyticsEngine): any {
        if (this.cacheMetrics[engine]) {
            return this.cacheMetrics[engine].index;
        } else {
            return null;
        }
    }

    getMetricLabel(engine: AnalyticsEngine, metric: string): string {
        if (!this.cacheMetrics[engine]) {
            return null;
        }
        const cache = this.cacheMetrics[engine].items;
        if (cache) {
            const cached = this.cacheMetricLabel.get(metric);
            if (cached) {
                return cached;
            } else {
                for (let x = 0; x < cache.length; x++) {
                    if (metric === cache[x].name) {
                        this.cacheMetricLabel.set(metric, cache[x].displayName);
                        return cache[x].displayName;
                    }
                }
            }
        }

        return null;
    }

    getSLAPresets(engine: AnalyticsEngine): Array<any> {
        if (this.cacheSLAPresets[engine]) {
            return this.cacheSLAPresets[engine];
        } else {
            return [];
        }
    }

    getPresetLabel(engine: AnalyticsEngine, preset: string): string {
        return null;
    }

    findMetric(engine: AnalyticsEngine, name: string, report?: string): any {
        const cache = this.cacheMetrics[engine];

        if (cache) {
            if (!cache.indexName) {
                // Build a full-text index of metric names that is used within
                // PickerDimension to do quick lookups of metrics, in order to get
                // their duplicate report values
                // See https://sprinklr.atlassian.net/browse/DISPLAY-2826
                cache.indexName = searchico(cache.items, {
                    case_sensitive: true,
                    hyper_indexing: false,
                    hyper_caching: false,
                    deep: false,
                    keys: ['unique'],
                });
            }

            return cache.indexName.find(name).find(item => {
                return (
                    item.name === name &&
                    (!report ||
                        item.report === report ||
                        (item.reportDups && item.reportDups.indexOf(report) != -1))
                );
            });
        } else {
            return null;
        }
    }

    private byDimensionsPromises = {};

    // Get all value labels for a dimension
    getValueLabels(
        engine: AnalyticsEngine,
        report: string,
        dimension: WidgetDimensionKey,
        additional?: any,
        filters?: Array<ReportingLookupFilter>,
        query?: string,
        limit?: number,
        page?: number
    ): Promise<Array<BulkLookupValue>> {
        // HACKTOWN: /byDimensions doesn't work for CAMPAIGN custom fields.  Have to look them
        // up a separate way
        if (engine === 'CAMPAIGN' && dimension.isCustom && this.cacheValueLabels.isEmpty()) {
            const dimensions = [
                { field: dimension.name, unique: dimension.name + ':' + dimension.name },
            ];
            return this.loadCampaignValueLabels(dimensions).then(response => {
                return this.getValueLabels2(
                    engine,
                    report,
                    dimension,
                    additional,
                    filters,
                    query,
                    limit,
                    page
                );
            });
        } else {
            // HACKTOWN: This will iterate 150k, 100k, and finally 50k to
            // find the limit that will result in a successful byDimensions call
            // This is short-lived.  Will be gone soon when pulling data on-demand
            // from byDimension api.
            return new Promise(async (resolve, reject) => {
                for (let x = 0; x < byDimensionLimits.length; x++) {
                    try {
                        const data = await this.getValueLabels2(
                            engine,
                            report,
                            dimension,
                            additional,
                            filters,
                            query,
                            limit,
                            page,
                            byDimensionLimits[x]
                        );

                        resolve(data);
                        break;
                    } catch (error) {
                        if (x === byDimensionLimits.length - 1) {
                            reject(error);
                        }
                    }
                }
            });
        }
    }

    // Get all value labels for a dimension
    private async getValueLabels2(
        engine: AnalyticsEngine,
        report: string,
        dimension: WidgetDimensionKey,
        additional?: any,
        lookupFilters?: Array<ReportingLookupFilter>,
        query?: string,
        limit?: number,
        page?: number,
        limitOverride?: number
    ): Promise<Array<BulkLookupValue>> {
        const filters = toJS(lookupFilters);

        if (filters?.length) {
            const clientIdFilter = filters.find(
                filter =>
                    filter.field === 'CLIENT_ID' &&
                    filter.values?.length &&
                    filter.values[0] === 'all'
            );

            if (clientIdFilter) {
                // console.log('doing an "all clients" lookup');
                const dimensionKey = WidgetDimensionKey.create(clientIdFilter);
                clientIdFilter.values = await this.getValueLabels(
                    engine,
                    report,
                    dimensionKey
                ).then(labels => labels.map(label => label.id));
            }
        }

        if (
            MetaInfoService.isDimLookupWhereFiltersAreIgnored(
                engine,
                report,
                dimension,
                filters,
                query
            )
        ) {
            return this.getValueLabelsForUnFilterableDims(engine, report, dimension, filters);
        }

        return new Promise((resolve, reject) => {
            if (engine === 'TWITTER') {
                switch (dimension.name) {
                    case 'WOEID':
                        resolve(
                            TrendService.twitterCountryOptions.map(countryOption => {
                                return {
                                    id: countryOption.value + '',
                                    name: countryOption.label,
                                };
                            })
                        );
                        return;
                }
            }

            // Custom dimension labels lookup does not work for CAMPAIGN engine
            const bypassCache =
                (!!filters && engine !== 'CAMPAIGN') || dimension.name === 'FROM_SN_USER';
            const unique = dimension.toString() + (additional ? JSON.stringify(additional) : '');

            const cached = bypassCache ? null : this.cacheValueLabels.get(unique);
            if (cached) {
                resolve(cached);
                return;
            }

            const lookupType = dimension.name;

            if (lookupType === 'BENCHMARKING_ACCOUNT_ID') {
                limit = 1000;
                // Champagne has ~38k brand ids as of 12/03/19
            } else if (lookupType === 'BRAND_ID') {
                limit = 50000;
                // Certain dimensions in BENCHMARKING fail if limit is > 20000
            } else if (engine === 'BENCHMARKING') {
                limit = 20000;
            }

            const args = {
                filters: filters || [],
                dimensionLookupRequests: [
                    {
                        lookupType: lookupType,
                        query: query || '',
                        page: {
                            page: page || 0,
                            size: limit || limitOverride || 150000, // 2021-1-28 -- up to 150K for Jack in the Box CITY lookup
                        },
                        additional: {
                            engine: engine,
                            reportName: report,
                        },
                    },
                ],
            };

            // If clientId is specified for this dimension, add it for the request, otherwise won't work
            //   https://sprinklr.atlassian.net/browse/DISPLAY-911
            if (dimension.clientId) {
                args.dimensionLookupRequests[0].additional['clientId'] = dimension.clientId;
            }

            // If we have an "additional" override, use it as-is
            if (additional) {
                args.dimensionLookupRequests[0].additional = additional;
            }

            // Add this if this is a custom field
            if (dimension.customName) {
                args.dimensionLookupRequests[0].additional['fieldName'] = dimension.customName;
            }

            // I know, using time ranges for value lookups seems retarded, but the Sprinklr API demands
            // at least for benchmarking:
            //  https://sprinklr.atlassian.net/browse/DISPLAY-1161
            // Using end-of-day so it can work as a cache key - if it's a different timestamp every call, no caching!
            const eod = moment(Date.now()).endOf('day');
            args.dimensionLookupRequests[0].additional['sinceTime'] = eod
                .clone()
                .subtract(1, 'month')
                .valueOf();
            args.dimensionLookupRequests[0].additional['untilTime'] = eod.valueOf();

            const promiseKey = JSON.stringify(args);

            if (!this.byDimensionsPromises[promiseKey]) {
                this.byDimensionsPromises[promiseKey] = this.axios.post(
                    'api/v3.1/reporting/insights/bulklookup/byDimensions',
                    args
                );
                // console.log('promise keys', {keys: Object.keys(this.byDimensionsPromises)});
            }

            return this.byDimensionsPromises[promiseKey]
                .then(response => {
                    if (
                        !response.data ||
                        !response.data[lookupType] ||
                        !response.data[lookupType].result
                    ) {
                        resolve([]);
                        return;
                    }

                    // const result = response.data[lookupType].result.filter((value) => {
                    let result = response.data[lookupType].result;

                    // Run a check to see if we need to convert the strange values format
                    // we get for dimensions like ACCOUNT_ID, AFFECTED_USER_ID and USER_ID
                    if (
                        result.length > 0 &&
                        (!result[0].name || dimension.name === 'MEDIA_ASSET_ID')
                    ) {
                        // Convert to the expected format
                        // Note: https://jsperf.com/fast-array-foreach
                        let x = result.length;
                        while (x--) {
                            if (typeof result[x] === 'object') {
                                MetaInfoService.normalizeBulkItem(result[x], dimension);
                                // Special case to deal with emjois response with is array of strings
                                //. https://sprinklr.atlassian.net/browse/DISPLAY-1663
                            } else if (typeof result[x] === 'string') {
                                result[x] = { id: result[x], name: result[x] };
                            }
                        }
                    }

                    result = response.data[lookupType].result.filter(value => {
                        if (typeof value === 'string') {
                            return true;
                        }
                        if (typeof value === 'object') {
                            // Filter out empty ids that Sprinklr sometimes returns
                            // (encountered when using custom fields for Santender)
                            if (value.id === undefined || value.id === null || value.id === '') {
                                return false;
                            }

                            // HACKTOWN: Stupid Sprinklr returns "Update" and "Twitter Update" with same id!
                            // Corie said to filter out the "Update" version,
                            // but that upset McDonalds and there are others, like "Instagram Update"
                            // so we are leaving "Update"
                            return (
                                lookupType !== 'SN_MESSAGE_TYPE' ||
                                value.id !== '2' ||
                                value.name === 'Update'
                            );
                        }
                        return false;
                    });

                    // Run a check to see if we need to convert the strange values format
                    // we get for dimensions like ACCOUNT_ID, AFFECTED_USER_ID and USER_ID
                    if (
                        result.length > 0 &&
                        (!result[0].name ||
                            dimension.name === 'MEDIA_ASSET_ID' ||
                            dimension.name === 'BENCHMARKING_ACCOUNT_ID')
                    ) {
                        // Convert to the expected format
                        // Note: https://jsperf.com/fast-array-foreach
                        let x = result.length;
                        while (x--) {
                            MetaInfoService.normalizeBulkItem(result[x], dimension);
                        }
                    }

                    if (!bypassCache) {
                        this.cacheValueLabels.set(unique, result);

                        // do indexed per-item caching for special non-filterable dimensions
                        // this allows subsequent filtered requests to take advantage of big unfiltered responses, saving
                        // many small network requests for data we've already fetched.
                        if (
                            result.length > 0 &&
                            MetaInfoService.unFilterableDimensions[dimension.name]
                        ) {
                            const dimCache = this.ensureUnFilterableDimensionsCache(dimension.name);
                            let x = result.length;
                            while (x--) {
                                dimCache[result[x].id] = result[x];
                            }
                        }
                    }

                    resolve(result);
                })
                .catch((error: any) => {
                    this.byDimensionsPromises[promiseKey] = null;
                    reject(error);
                });
        });
    }

    // Handle filtered bulklookup/byDimensions requests that have no filter capability in core.
    // Requests for dim name "LST_THEME" and "TOPIC_TAGS" both fall into this category - for the code paths from
    // bulklookup/byDimensions the filters param is completely ignored and full, unfiltered lists are returned.
    // see these classes in sprinklr main codebase:
    // com.spr.shifu.lookup.impl.listening.TopicTagLookup
    // com.spr.shifu.lookup.impl.listening.ListeningThemeLookup
    // when the filtering is like this:
    // {
    //   "filters": [
    //     {
    //       "field": "TOPIC_TAGS",
    //       "filterType": "IN",
    //       "values": [
    //         "CL - All of Dell - GL",
    //         "ET - All of Dell - GL"
    //       ]
    //     }
    //   ],
    //   "dimensionLookupRequests": [
    //     {
    //       "lookupType": "TOPIC_TAGS",
    //       "query": "",
    //       "page": {
    //         "page": 0,
    //         "size": 20000
    //       },
    //       "additional": {
    //         "engine": "LISTENING",
    //         "reportName": "SPRINKSIGHTS",
    //         "sinceTime": 1563778799999,
    //         "untilTime": 1566457199999
    //       }
    //     }
    //   ]
    // }
    // We're asking for a lookup for the individual listed items, but we'll get back the full list up to page.size and
    // in the case of Dell this can take upwards of 5 seconds (+20K items) and wasn't cached on our side due to presence
    // of the filters - so special-case it, use caching where we can and do the query better when there's a cache miss.
    private getValueLabelsForUnFilterableDims(
        engine: AnalyticsEngine,
        report: string,
        dimension: WidgetDimensionKey,
        filters?: ReportingLookupFilter[]
    ): Promise<Array<BulkLookupValue>> {
        return new Promise((resolve, reject) => {
            const lookupType = dimension.name;
            const keyFilter =
                filters && filters.find(f => f.filterType === 'IN' && f.field === lookupType);

            const keys = keyFilter ? (keyFilter as ReportingLookupFilter).values : [];

            if (this.cacheUnFilterableDimensions[lookupType]) {
                const cachedResults = keys.map(
                    key => this.cacheUnFilterableDimensions[lookupType][key]
                );

                if (keys.length === cachedResults.length) {
                    // found everything we need!
                    return cachedResults;
                }
            }

            // this must match com.spr.reporting.rest.LookupRequest from sprinklr/shifu/src/main/java/com/spr/reporting/rest/LookupRequest.java
            const args = {
                lookupType: dimension.name,
                keys,
                extraParams: {
                    engine,
                    report,
                    deleted: false, // ??
                    inaccessible: true, // ??
                },
            };

            const promiseKey = JSON.stringify(args);

            // note we're using bulklookup, not bulklookup/byDimensions. Query format is different.

            const lookupPromise = this.axios.post('api/v3.1/reporting/insights/bulklookup', args);

            return lookupPromise.then(response => {
                if (
                    !response.data ||
                    !response.data[lookupType] ||
                    !response.data[lookupType].lookupValues
                ) {
                    resolve([]);
                }

                const result = keys.map(key => response.data[lookupType].lookupValues[key]);

                const dimCache = this.ensureUnFilterableDimensionsCache(dimension.name);
                let x = result.length;
                while (x--) {
                    dimCache[result[x].id] = result[x];
                }

                resolve(result);
            });
        });
    }

    static isDimLookupWhereFiltersAreIgnored(
        engine: AnalyticsEngine,
        report: string,
        dimension: WidgetDimensionKey,
        filters?: Array<ReportingLookupFilter>,
        query?: string
    ): boolean {
        if (query) {
            return false; // don't mess up a text query, like complete-as-you-type list searches
        }

        if (
            engine !== 'LISTENING' ||
            report !== 'SPRINKSIGHTS' ||
            !dimension ||
            !filters ||
            filters.length > 1
        ) {
            return false;
        }

        // This method is only valid for id-based lookups, so make sure it's an IN filter looking at the dimension name.

        const filter: ReportingLookupFilter = filters[0];
        if (!filter || filter.filterType !== 'IN') {
            return false;
        }

        return (
            MetaInfoService.unFilterableDimensions[dimension.name] &&
            filter.field === dimension.name
        );
    }

    private ensureUnFilterableDimensionsCache(cacheName: string): any {
        if (!this.cacheUnFilterableDimensions[cacheName]) {
            this.cacheUnFilterableDimensions[cacheName] = {};
        }

        return this.cacheUnFilterableDimensions[cacheName];
    }

    private isRequestForPresetNeeded(engine: AnalyticsEngine): boolean {
        // TODO when trying to do this differently, was hitting similar to https://github.com/Microsoft/TypeScript/issues/11533
        if (!engine) {
            return false;
        } else if (this.cacheSLAPresets[engine]) {
            return false;
        } else if (engine === 'PLATFORM') {
            return true;
        } else if (engine === 'BENCHMARKING') {
            return true;
        }

        return false;
    }

    loadSLAPresets(engine: AnalyticsEngine): void {
        const promise = new Promise<void>((resolve, reject) => {
            if (!this.isRequestForPresetNeeded(engine)) {
                resolve();
            } else {
                this.axios
                    .get('api/v3.1/reports/v6/config/sla')
                    .then((result: any) => {
                        this.cacheSLAPresets[engine] = result.data;
                        resolve();
                    })
                    .catch((error: any) => {
                        reject(error);
                        delete this.cachePromises[engine];
                    });
            }
        });

        MetaInfoService.loaderSLAPresets = fromPromise(promise);
    }

    loadDashboards(): Promise<MonitoringDashboard[]> {
        const promise: Promise<MonitoringDashboard[]> = new Promise((resolve, reject) => {
            if (this.dashboards) {
                resolve(this.dashboards);
            } else {
                const q = `
                    {
                        client {
                            monitoringDashboards {
                                id
                                name
                                locked
                                shared
                                columnOrder
                                columns {
                                    id
                                    dashboardId
                                    name
                                    description
                                    ownerUserId
                                    channel
                                    type
                                    sourceType
                                }
                            }
                        }
                    }`;

                return this.graphQLService
                    .query({
                        query: q,
                        variables: null,
                    })
                    .then((response: any): void => {
                        this.dashboards = response.client.monitoringDashboards;
                        resolve(response.client.monitoringDashboards);
                    })
                    .catch((error: any) => {
                        if (error && error.data && error.data.errors) {
                            console.error('loadDashboards: ', JSON.stringify(error.data.errors));
                        }

                        reject(error);
                    });
            }
        });

        MetaInfoService.loaderDashboards = fromPromise(promise);

        return promise;
    }

    loadDimensions(engine: string): void {
        const promise = new Promise<void>((resolve, reject) => {
            if (this.cachePromises[engine]) {
                this.cachePromises[engine].then(resolve, reject);
                return;
            }

            // Monitoring dashboard is not an analytics engine.  Due to inheritance chain, however,
            // this value can end up being passed here.  So ignore it.
            if (!engine || this.cacheDimensions[engine] || engine === 'MONITORING_DASHBOARD') {
                resolve();
            } else {
                let url: string;

                // CAMPAIGN engine requires a different call to get dimensions for campaign posts.
                // If you use the "reports" version, you will get dimensions for campaigns, which isn't right
                if (engine === 'CAMPAIGN') {
                    url = 'api/v3.1/content/allFacets?contentType=POST';
                } else {
                    url = 'api/v3.1/reports/v6/metadata/' + engine;
                }

                this.cachePromises[engine] = this.axios
                    .get(url)
                    .then((result: any) => {
                        this.cacheDimensionsAndMetrics(engine, result.data).then((result: any) => {
                            resolve();
                        });
                    })
                    .catch((error: any) => {
                        delete this.cachePromises[engine];
                        reject(error);
                    });
            }
        });

        MetaInfoService.loaderDimensions[engine] = fromPromise(promise);
    }

    getUserAccessibleAccountIds() {
        if (this.cacheAccounts) {
            return this.cacheAccounts;
        }

        const url = 'api/v3.1/bootstrap/resources?types=USER_ACCESSIBLE_CLIENT_ACCOUNT_IDS';

        this.cacheAccounts = this.axios
            .get(url)
            .then((result: any) => {
                const unique = {};

                const data = result?.data?.USER_ACCESSIBLE_CLIENT_ACCOUNT_IDS;
                if (data) {
                    for (const clientId in data) {
                        const accountIds = data[clientId];
                        accountIds.forEach(accountId => (unique[accountId] = true));
                    }

                    return Object.keys(unique);
                }

                return null;
            })
            .catch((error: any) => {
                console.log('Uanble to load user accessible accounts');
            });

        return this.cacheAccounts;
    }

    private cacheDimensionsAndMetrics(engine, data): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            const sort = (a: any, b: any) => {
                const nameA = a.displayName.toUpperCase();
                const nameB = b.displayName.toUpperCase();

                if (nameA < nameB) {
                    return -1;
                } else if (nameA > nameB) {
                    return 1;
                } else {
                    return 0;
                }
            };

            if (engine === 'CAMPAIGN') {
                data = data.POST;

                // Normalize these dimensions to look like the other engines
                let x = data.length;
                while (x--) {
                    data[x].name = data[x].field;
                    data[x].type = data[x].fieldType === 'filter' ? 'NORMAL' : 'CUSTOM';
                    data[x].filterDimension = true;
                }

                // Add this synthentic metric so we can show in post cards and sort by this
                data.push({
                    name: 'SCHEDULE_DATE',
                    fieldType: 'NORMAL',
                    displayName: 'Schedule Date',
                    filterDimension: false,
                });

                // Add this synthentic metric.  It's not in /allFacets list but it
                // does appear in Additional Properties in Sprinklr UI.
                data.push({
                    name: 'THEME_ID',
                    fieldType: 'NORMAL',
                    displayName: 'Theme',
                    filterDimension: true,
                });

                // Do a deep copy so that processing for measurement doesn't impact
                // dimensions (ie. "unique" post-processing)
                data = { CAMPAIGN: { dimensions: data, measurements: ObjectUtils.copy(data) } };
            }

            const metrics = this.getMetricsCombined(engine, data, sort);

            // Build a full-text index of metric labels that is used with a custom
            // filter with the react-select control.  Needed this approach once
            // we started supporting PAID which has 67k metrics and the original
            // react-select approach completely choked.
            const index = searchico(metrics, {
                hyper_indexing: false,
                hyper_caching: false,
                keys: ['displayName'],
            });

            this.cacheMetrics[engine] = { items: metrics, index: index, indexName: null };

            this.buildDimensions(engine, data, sort);

            if (engine === 'CAMPAIGN') {
                const dimensions = this.loadCampaignValueLabels(data.CAMPAIGN.dimensions)
                    .then(response => {
                        resolve();
                    })
                    .catch((error: any) => {
                        reject(error);
                    });
            } else {
                resolve();
            }
        });
    }

    private getDimensionCache(
        engine: string,
        report?: string,
        type?: 'all' | 'normal' | 'custom' | 'normalFilters' | 'customFilters'
    ): any {
        const cache = this.cacheDimensions[engine];

        if (cache) {
            if (report && type) {
                if (cache[report] && cache[report][type]) {
                    return cache[report][type];
                }
            } else if (report) {
                if (cache[report]) {
                    return cache[report];
                }
            } else {
                return cache;
            }
        }

        return null;
    }

    // Combine all metrics across all reports in result as assign each one the report in came from
    private getMetricsCombined(
        engine: string,
        data: any,
        sort: (a: any, b: any) => number
    ): Array<any> {
        const result: Array<any> = [];
        let measurement: any;
        let report: any;
        let x: number;
        const filterDuplicates = engine === 'PAID';
        const duplicateCheck = {};
        let firstReport: string;

        const addMeasurement = (key: string) => {
            report = data[key];

            x = report.measurements.length;

            while (x--) {
                measurement = report.measurements[x];

                if (!filterDuplicates || !duplicateCheck[measurement.name]) {
                    measurement.unique = measurement.name + ':' + key;
                    measurement.report = key;
                    result.push(measurement);
                }

                if (filterDuplicates) {
                    if (!duplicateCheck[measurement.name]) {
                        duplicateCheck[measurement.name] = measurement;
                    } else if (!duplicateCheck[measurement.name].reportDups) {
                        duplicateCheck[measurement.name].reportDups = [key];
                    } else {
                        duplicateCheck[measurement.name].reportDups.push(key);
                    }
                }
            }
        };

        // Sort the reports with the lowest priority report first so that duplicate measurements
        // are filtered out.  This is a requirement for PAID.
        const keys = this.sortReportsByPriority(data);

        keys.forEach(key => {
            addMeasurement(key);
        });

        result.sort(sort);

        return result;
    }

    private sortReportsByPriority(data: any): string[] {
        return Object.keys(data).sort((a, b) => {
            const first = data[a].priority;
            const second = data[b].priority;
            return first > second ? 1 : second > first ? -1 : 0;
        });
    }

    // Separate out all dimensions into different categories for each report
    private buildDimensions(engine: string, data: any, sort: (a: any, b: any) => number): void {
        const allDimensions = [];

        const filter = (report: string, type: FilterType, types: string[], item: any) => {
            // Set the report so that we can access it when processing duplicate groupbys
            // for PAID (DISPLAY-2826)
            item.report = report;
            if (!item.displayName) {
                item.displayName = item.name;
            }

            if (type === FilterType.Measurement && !item.dataType) {
                item.dataType = 'NUMERIC';
            }

            // making sure topic cluster makes it through
            const whiteList =
                item.uniqueKey === 'D_TITLE_TOPIC_CLUSTER' ||
                item.uniqueKey === 'D_MESSAGE_TOPIC_CLUSTER' ||
                (item.groupByNotAllowed && item.name?.indexOf('TOPIC_CLUSTER') != -1);
            const blackList = item.uniqueKey === 'D_TOPIC_CLUSTER';
            const filterDimension = type === FilterType.Filter;
            return (
                (!filterDimension || filterDimension === item.filterDimension) &&
                (filterDimension || (!!whiteList && !!!blackList) || !item.groupByNotAllowed) &&
                !item.hidden &&
                types.indexOf(item.type) !== -1
            );
        };

        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const report = data[key];

                const dimensions = report.dimensions;
                const measurements = report.measurements;

                if (!this.cacheDimensions[engine]) {
                    this.cacheDimensions[engine] = {};
                }

                let cache = this.cacheDimensions[engine][key];
                if (!cache) {
                    cache = {
                        all: null,
                        normal: null,
                        normalFilters: null,
                        custom: null,
                        customFilters: null,
                    };

                    this.cacheDimensions[engine][key] = cache;
                }

                let measurementFilters = [];
                if (this.authService.engineMeasurementFilter[engine])
                    measurementFilters = measurements.filter(
                        filter.bind(null, key, FilterType.Measurement, [
                            'CUMULATIVE',
                            'CARDINALITY',
                            'SCRIPT',
                        ])
                    );

                cache.all = dimensions.concat(measurementFilters).sort((a, b) => {
                    const c = a.name + (a.childName ? ':' + a.childName : '');
                    const d = b.name + (b.childName ? ':' + b.childName : '');
                    return c > d ? 1 : d > c ? -1 : 0;
                });

                cache.normal = dimensions.filter(
                    filter.bind(null, key, FilterType.None, [
                        'NORMAL',
                        'GROUP',
                        'SCRIPT',
                        'NESTED',
                        'CALCULATED',
                    ])
                );
                cache.normal.sort(sort);
                cache.normalFilters = dimensions.filter(
                    filter.bind(null, key, FilterType.Filter, [
                        'NORMAL',
                        'GROUP',
                        'SCRIPT',
                        'NESTED',
                        'CALCULATED',
                    ])
                );
                cache.normalFilters = cache.normalFilters.concat(measurementFilters).sort(sort);
                cache.custom = dimensions.filter(
                    filter.bind(null, key, FilterType.None, ['CUSTOM'])
                );
                cache.custom.sort(sort);
                this.uniquelyIndentify(cache.custom);
                cache.customFilters = dimensions.filter(
                    filter.bind(null, key, FilterType.Filter, ['CUSTOM'])
                );
                cache.customFilters.sort(sort);
                this.uniquelyIndentify(cache.customFilters);

                allDimensions.push.apply(allDimensions, dimensions);
            }
        }

        // Build a full-text index of metric names that is used within
        // PickerDimension to do quick lookups of metrics, in order to get
        // their duplicate report values
        // See https://sprinklr.atlassian.net/browse/DISPLAY-2826
        this.indexDimensions[engine] = searchico(allDimensions, {
            case_sensitive: true,
            hyper_indexing: false,
            hyper_caching: false,
            deep: false,
            keys: ['name'],
        });
    }

    // Encountered with client Santender.  They have duplicate custom fields with the same name
    // like "Promotion", which is confusing to see in the UI.  I now append the custom field type
    // like "inbound" and "outbound" to clarify which is which.  Sprinklr's own UI currently
    // just shows the duplicates without any clarification.  Lame!
    private uniquelyIndentify(items: Array<any>): void {
        let x = items.length;
        while (x--) {
            const item = items[x];

            WidgetDimensionKey.customUniquelyIndentify(item);

            if (x > 0 && item.displayName === items[x - 1].displayName) {
                this.renameUnique(item);
                this.renameUnique(items[x - 1]);
            }
        }
    }

    private renameUnique(item: any): void {
        if (item.name.indexOf('INBOUND') === 0) {
            item.displayName += ' (inbound)';
        } else if (item.name.indexOf('OUTBOUND') === 0) {
            item.displayName += ' (outbound)';
        }
    }

    private loadCampaignValueLabels(dimensions: any[]): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            // CAMPAIGN engine requires a different call to get custom dimensions values
            const url = 'api/v3.1/bootstrap/resources?types=OUTBOUND_CUSTOM_FIELDS';

            this.axios
                .get(url)
                .then((result: any) => {
                    const custom = result.data.OUTBOUND_CUSTOM_FIELDS;

                    // Pre-populate the values for these custom dimensions in the cache
                    let x = custom.length;
                    while (x--) {
                        if (custom[x].options) {
                            const found = dimensions.find(
                                item => item.field === custom[x].fieldName
                            );
                            if (found) {
                                const labels = custom[x].options.map(item => {
                                    return { id: item, name: item };
                                });
                                this.cacheValueLabels.set(found.unique, labels);
                            }
                        }
                    }

                    resolve();
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }
}
